import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';

import { Toast } from '@/components/common';
import StepIndicator from './components/StepIndicator';
import BasicInfoForm, { BasicInfoFormData } from './components/BasicInfoForm';
import ContentUploadForm from './components/ContentUploadForm';
import EpisodeDetailsForm from './components/EpisodeDetailsForm';
import CopyrightForm from './components/CopyrightForm';
import BusinessModelForm from './components/BusinessModelForm';
import CastForm, { CastMemberInfo } from './components/CastForm';
import { useChannelStore } from '@/stores';
import {
  fetchCreateApplicationDraft,
  fetchGetApplicationDetail,
  fetchSaveStep1,
  fetchSaveStep2,
  fetchSaveStep3,
  fetchSaveStep4,
  fetchSaveStep5,
  fetchSaveStep6,
  fetchSubmitApplication,
} from '@/service/api';

export default function ApplyPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryId = searchParams.get('id');
  const queryStepStr = searchParams.get('step');

  const initialStep = useMemo(() => {
    if (queryStepStr) {
      const num = parseInt(queryStepStr, 10);
      if (!isNaN(num) && num >= 1 && num <= 6) {
        return num - 1;
      }
    }
    return 0;
  }, [queryStepStr]);

  const [activeStep, setActiveStep] = useState(initialStep);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'saving' | 'submitting' | 'success'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // 保持最新 applicationId 的 ref 与 state 同步，规避闭包异步竞态问题
  const [applicationId, setApplicationIdState] = useState<string | null>(null);
  const applicationIdRef = useRef<string | null>(null);
  const fetchedDetailIdRef = useRef<string | null>(null);

  const [toast, setToast] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  const showToast = (text: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3000);
  };

  const syncStepUrl = useCallback((targetStep: number, targetId?: string | null) => {
    setActiveStep(targetStep);
    const appId = targetId !== undefined ? targetId : applicationIdRef.current;
    const stepVal = targetStep + 1;
    const currentParams = new URLSearchParams(window.location.search);
    const currentStepStr = currentParams.get('step');
    const currentIdStr = currentParams.get('id');

    const targetStepStr = String(stepVal);
    const targetIdStr = appId || null;

    if (currentStepStr === targetStepStr && currentIdStr === targetIdStr) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    params.set('step', targetStepStr);
    if (appId) {
      params.set('id', appId);
    } else {
      params.delete('id');
    }

    navigate(`/apply?${params.toString()}`, { replace: true });
  }, [navigate]);

  const updateApplicationId = (id: string | null) => {
    applicationIdRef.current = id;
    setApplicationIdState(id);
    if (id && searchParams.get('id') !== id) {
      syncStepUrl(activeStep, id);
    }
  };

  // 响应 URL 步骤参数变化（如回退/前进）
  useEffect(() => {
    if (queryStepStr) {
      const num = parseInt(queryStepStr, 10);
      if (!isNaN(num) && num >= 1 && num <= 6) {
        const stepIdx = num - 1;
        if (stepIdx !== activeStep) {
          setActiveStep(stepIdx);
        }
      }
    }
  }, [queryStepStr]);

  // 自动保存状态与文案
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [autoSaveTip, setAutoSaveTip] = useState<string>('');

  // 基础数据选项
  const { channels: channelList, loading: loadingChannels, fetchChannels } = useChannelStore();

  // 各步骤真实结构表单状态 (Api.Application.*)
  const [step1Data, setStep1Data] = useState<BasicInfoFormData>({
    title: '',
    description: '',
    content_type_code: 'SHORT_DRAMA',
    source_language_code: 'zh-CN',
    orientation: 'VERTICAL',
    audience_code: 'GENERAL',
    genre_codes: [],
    contract_id: '',
    cover_asset_id: '',
    has_live_actor: true,
    is_ai_content: false,
  });

  const [step2Data, setStep2Data] = useState<Api.Application.Step2Request>({
    attachments: [],
  });

  const [step3Data, setStep3Data] = useState<Api.Application.Step3Request>({
    episodes: [{ episode_no: 1, episode_title: '', upload_asset_id: '' }],
  });

  const [step4Data, setStep4Data] = useState<Api.Application.Step4Request>({
    declaration_confirmed: true,
    is_adapted_work: false,
    is_original_right_holder: true,
    material_right_holder_name: '',
    material_work_title: '',
    attachments: [],
  });

  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [releaseNote, setReleaseNote] = useState<string>('');
  const [castList, setCastList] = useState<CastMemberInfo[]>([
    { name: '', role: '', upload_asset_id: '', bio: '' },
  ]);

  const [agreed, setAgreed] = useState(false);

  // 根据 URL queryId 加载已有草稿详情
  useEffect(() => {
    if (queryId && fetchedDetailIdRef.current !== queryId) {
      fetchedDetailIdRef.current = queryId;
      applicationIdRef.current = queryId;
      setApplicationIdState(queryId);
      setAutoSaveStatus('saving');
      setAutoSaveTip('正在加载草稿数据...');
      fetchGetApplicationDetail(queryId)
        .then((detail: any) => {
          if (detail) {
            const coverAccessUrl = detail.cover?.access_url || '';
            setStep1Data({
              title: detail.title || '',
              description: detail.description || '',
              content_type_code: detail.content_type_code || 'SHORT_DRAMA',
              source_language_code: detail.source_language_code || 'zh-CN',
              orientation: detail.orientation || 'VERTICAL',
              audience_code: detail.audience_code || 'GENERAL',
              genre_codes: detail.genre_codes || [],
              contract_id: detail.contract_subject_id || detail.project_id || '',
              cover_asset_id: detail.cover?.id || '',
              cover_url: coverAccessUrl,
              subtitle: detail.subtitle || '',
              has_live_actor: detail.has_live_actor ?? true,
              is_ai_content: detail.is_ai_content ?? false,
            } as any);
            if (detail.cast_or_role_info_json && Array.isArray(detail.cast_or_role_info_json)) {
              setCastList(detail.cast_or_role_info_json);
            }
            if (detail.attachments || detail.cover) {
              setStep2Data({
                attachments: (detail.attachments || []).map((att: any) => ({
                  category: att.category || 'MAIN_VIDEO',
                  upload_asset_id: att.upload_asset_id || att.upload_asset?.id || '',
                  upload_asset: att.upload_asset,
                  access_url: att.upload_asset?.access_url || att.access_url || '',
                  display_name: att.display_name,
                  visibility: att.visibility || 'FRONTEND_VISIBLE',
                })),
                cover_asset_id: detail.cover?.id || '',
                cover_url: coverAccessUrl,
              } as any);
            }
            if (detail.episodes && Array.isArray(detail.episodes)) {
              setStep3Data({
                episodes: detail.episodes.map((ep: any) => ({
                  episode_no: ep.episode_no || 1,
                  episode_title: ep.episode_title || '',
                  upload_asset_id: ep.upload_asset_id || ep.current_file?.id || ep.upload_asset?.id || '',
                  upload_asset: ep.current_file || ep.upload_asset,
                  access_url: ep.current_file?.access_url || ep.upload_asset?.access_url || ep.access_url || '',
                })),
              });
            }
            if (detail.copyright) {
              const rawAtts = detail.copyright.attachments || [];
              const hasAuxiliary = rawAtts.some((att: any) => att.category === 'COPYRIGHT_AUXILIARY');
              const isAdapted = Boolean(detail.copyright.is_adapted_work ?? hasAuxiliary);
              const isOriginal = Boolean(detail.copyright.is_original_right_holder ?? true);

              setStep4Data({
                declaration_confirmed: detail.copyright.declaration_confirmed ?? true,
                is_adapted_work: isAdapted,
                is_original_right_holder: isOriginal,
                material_right_holder_name: detail.copyright.material_right_holder_name || '',
                material_work_title: detail.copyright.material_work_title || '',
                attachments: rawAtts.map((att: any) => ({
                  category: att.category || (isAdapted ? 'COPYRIGHT_AUXILIARY' : 'COPYRIGHT_CORE'),
                  upload_asset_id: att.upload_asset_id || att.upload_asset?.id || '',
                  upload_asset: att.upload_asset,
                  access_url: att.upload_asset?.access_url || att.access_url || '',
                  display_name: att.display_name,
                  visibility: att.visibility || 'FRONTEND_VISIBLE',
                })),
              });
            }
            if (detail.channel_intents && Array.isArray(detail.channel_intents)) {
              setSelectedChannels(detail.channel_intents.map((ci: any) => ci.channel_id || ci.channel_code).filter(Boolean));
            }
            if (detail.release_request_note) {
              setReleaseNote(detail.release_request_note);
            }
            if (detail.application_declaration_confirmed) {
              setAgreed(true);
            }
            setAutoSaveStatus('saved');
            setAutoSaveTip('草稿数据加载完成');
          }
        })
        .catch((err) => {
          console.error('加载草稿数据失败:', err);
          setAutoSaveStatus('error');
          setAutoSaveTip('加载草稿数据失败');
        });
    }
  }, [queryId]);

  // 加载下拉/渠道列表数据
  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  const scrollToTop = () => {
    window.scrollTo({ top: 120, behavior: 'smooth' });
  };

  const validateCurrentStep = (step: number): boolean => {
    switch (step) {
      case 0:
        if (!isStep1Valid()) {
          showToast('请填写完整基础信息必填项（作品名称、内容类型、源语种、画幅朝向、受众群体、题材分类及剧集描述）', 'error');
          return false;
        }
        return true;
      case 1:
        if (!isStep2Valid()) {
          showToast('请上传项目封面图及至少一项宣发物料', 'error');
          return false;
        }
        return true;
      case 2:
        if (!isStep3Valid()) {
          showToast('请确保至少包含一集，且每集标题与视频文件均已上传', 'error');
          return false;
        }
        return true;
      case 3:
        if (!isStep4Valid()) {
          showToast('请填写完整的版权信息及著作权声明，并上传版权证明文件', 'error');
          return false;
        }
        return true;
      case 4:
        if (!isStep5Valid()) {
          showToast('请至少选择一个意向发行渠道', 'error');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleNextStep = async () => {
    if (validateCurrentStep(activeStep)) {
      let appId = applicationIdRef.current;
      if (activeStep === 0 && !appId) {
        appId = await ensureDraftCreated();
      }
      const nextStep = Math.min(activeStep + 1, 5);
      syncStepUrl(nextStep, appId);
      scrollToTop();
    }
  };

  const handlePrevStep = () => {
    const prevStep = Math.max(activeStep - 1, 0);
    syncStepUrl(prevStep);
    scrollToTop();
  };

  const handleStepClick = async (targetStep: number) => {
    if (targetStep < activeStep) {
      syncStepUrl(targetStep);
      scrollToTop();
    } else if (targetStep > activeStep) {
      if (validateCurrentStep(activeStep)) {
        let appId = applicationIdRef.current;
        if (activeStep === 0 && !appId) {
          appId = await ensureDraftCreated();
        }
        syncStepUrl(targetStep, appId);
        scrollToTop();
      }
    }
  };

  // 校验并合成包含 cast_or_role_info_json 的 Step1 提交数据
  const getCombinedStep1Request = (s1Data = step1Data, currentCast = castList): Api.Application.Step1Request => {
    return {
      title: s1Data.title,
      description: s1Data.description,
      content_type_code: s1Data.content_type_code || 'SHORT_DRAMA',
      source_language_code: s1Data.source_language_code || 'zh-CN',
      genre_codes: s1Data.genre_codes || [],
      orientation: s1Data.orientation || 'VERTICAL',
      audience_code: s1Data.audience_code || 'GENERAL',
      subtitle: s1Data.subtitle,
      has_live_actor: s1Data.has_live_actor ?? true,
      is_ai_content: s1Data.is_ai_content ?? false,
      total_episode_count: step3Data.episodes?.length || 1,
      cast_or_role_info_json: currentCast.filter((c) => c.name || c.role),
    };
  };

  // 核心封面校验：检测步骤二中的项目封面图 (APPLICATION_COVER / cover_asset_id)
  const hasCoverAsset = (s1 = step1Data, s2 = step2Data): boolean => {
    const coverInStep1 = s1.cover_asset_id?.trim();
    const coverInStep2 = s2.cover_asset_id?.trim() || (s2.attachments || []).find((a) => a.category === 'APPLICATION_COVER')?.upload_asset_id?.trim();
    return Boolean(coverInStep1 || coverInStep2);
  };

  // 步骤一必填项校验：剧集名、内容类型、源语种、画面朝向、受众人群、题材分类及描述
  const isStep1Valid = (s1 = step1Data): boolean => {
    return Boolean(
      s1.title?.trim() &&
      s1.content_type_code?.trim() &&
      s1.source_language_code?.trim() &&
      s1.orientation?.trim() &&
      s1.audience_code?.trim() &&
      (s1.genre_codes && s1.genre_codes.length > 0) &&
      s1.description?.trim()
    );
  };

  // 步骤二必填项校验：必须包含项目封面图 (APPLICATION_COVER)，且至少有一个已完成上传的宣发资料附件
  const isStep2Valid = (s2 = step2Data): boolean => {
    const hasCover = hasCoverAsset(step1Data, s2);
    const hasAttachment = (s2.attachments || []).some((att) => Boolean(att.upload_asset_id?.trim()));
    return hasCover && hasAttachment;
  };

  // 步骤三必填项校验：至少有一集，且所有剧集的标题与视频文件均已上传填写完成
  const isStep3Valid = (s3 = step3Data): boolean => {
    const eps = s3.episodes || [];
    if (eps.length === 0) return false;
    return eps.every((ep) => Boolean(ep.episode_title?.trim() && ep.upload_asset_id?.trim()));
  };

  // 步骤四必填项校验：权利方名称、原著作品名均非空、且包含版权证明文件
  const isStep4Valid = (s4 = step4Data): boolean => {
    const hasDoc = (s4.attachments || []).some((att) => Boolean(att.upload_asset_id?.trim()));
    return Boolean(
      s4.material_right_holder_name?.trim() &&
      s4.material_work_title?.trim() &&
      hasDoc &&
      s4.declaration_confirmed !== false
    );
  };

  // 步骤五必填项校验：至少选择一个目标发行渠道
  const isStep5Valid = (channels = selectedChannels): boolean => {
    return channels.length > 0;
  };

  // 步骤六必填项校验：协议与确认声明勾选完成
  const isStep6Valid = (isAgreed = agreed): boolean => {
    return isAgreed === true;
  };

  // 确保草稿已创建，若无且满足步骤一必填项校验则自动调用创建草稿接口
  const ensureDraftCreated = async (currentStep1?: BasicInfoFormData, currentCast?: CastMemberInfo[]): Promise<string | null> => {
    if (applicationIdRef.current) {
      return applicationIdRef.current;
    }
    const s1 = currentStep1 || step1Data;
    if (!isStep1Valid(s1)) {
      return null;
    }

    const step1Payload = getCombinedStep1Request(s1, currentCast);
    setAutoSaveStatus('saving');
    setAutoSaveTip('正在自动创建申请草稿...');
    try {
      const res = await fetchCreateApplicationDraft(step1Payload);
      if (res?.id) {
        fetchedDetailIdRef.current = res.id;
        updateApplicationId(res.id);
        setAutoSaveStatus('saved');
        setAutoSaveTip('草稿已自动创建');
        showToast('已自动为您创建申请草稿', 'success');
        return res.id;
      }
    } catch (err) {
      console.error('自动创建草稿失败:', err);
      setAutoSaveStatus('error');
      setAutoSaveTip('自动创建草稿失败');
      showToast('自动创建草稿失败', 'error');
    }
    return null;
  };

  // 步骤 1 基础信息变更（仅在步骤一所有必填项填写完整后自动保存）
  const step1TimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleStep1Change = (newData: BasicInfoFormData) => {
    setStep1Data(newData);

    if (step1TimerRef.current) clearTimeout(step1TimerRef.current);
    step1TimerRef.current = setTimeout(async () => {
      if (!isStep1Valid(newData)) {
        return;
      }
      const appId = await ensureDraftCreated(newData);
      if (appId) {
        setAutoSaveStatus('saving');
        setAutoSaveTip('正在自动保存基础信息...');
        try {
          const payload = getCombinedStep1Request(newData);
          await fetchSaveStep1(appId, payload);
          setAutoSaveStatus('saved');
          setAutoSaveTip('基础信息已自动保存');
          showToast('步骤一：基础信息已自动保存', 'success');
        } catch (err) {
          console.error('保存基础信息失败:', err);
          setAutoSaveStatus('error');
          setAutoSaveTip('基础信息保存失败');
          showToast('基础信息保存失败', 'error');
        }
      }
    }, 600);
  };

  // 步骤 2 内容物料上传自动保存（必须先上传项目封面后触发）
  const handleStep2Change = async (newData: Api.Application.Step2Request) => {
    setStep2Data(newData);
    const coverId = newData.cover_asset_id || (newData.attachments || []).find((a) => a.category === 'APPLICATION_COVER')?.upload_asset_id;
    if (coverId && coverId !== step1Data.cover_asset_id) {
      setStep1Data((prev) => ({ ...prev, cover_asset_id: coverId }));
    }
    if (!isStep2Valid(newData)) {
      return;
    }
    setAutoSaveStatus('saving');
    setAutoSaveTip('正在自动保存上传物料...');
    try {
      let appId = applicationIdRef.current;
      if (!appId) {
        appId = await ensureDraftCreated();
      }
      if (appId) {
        await fetchSaveStep2(appId, {
          cover_asset_id: coverId || step1Data.cover_asset_id,
          attachments: newData.attachments || [],
        });
        setAutoSaveStatus('saved');
        setAutoSaveTip('上传物料已自动保存');
        showToast('步骤二：宣发资料已自动保存', 'success');
      }
    } catch (err: any) {
      console.error('保存步骤二物料失败:', err);
      setAutoSaveStatus('error');
      setAutoSaveTip('物料自动保存失败');
      showToast('宣发资料保存失败', 'error');
    }
  };

  // 步骤 3 剧集详情/文件上传自动保存（仅在所有剧集标题与文件非空时触发）
  const handleStep3Change = async (newData: Api.Application.Step3Request) => {
    setStep3Data(newData);
    if (!isStep3Valid(newData)) {
      return;
    }
    setAutoSaveStatus('saving');
    setAutoSaveTip('正在自动保存剧集内容...');
    try {
      let appId = applicationIdRef.current;
      if (!appId) {
        appId = await ensureDraftCreated();
      }
      if (appId) {
        await fetchSaveStep3(appId, newData);
        setAutoSaveStatus('saved');
        setAutoSaveTip('剧集内容已自动保存');
        showToast('步骤三：剧集内容已自动保存', 'success');
      }
    } catch (err: any) {
      console.error('保存步骤三剧集失败:', err);
      setAutoSaveStatus('error');
      setAutoSaveTip('剧集内容自动保存失败');
      showToast('剧集内容保存失败', 'error');
    }
  };

  // 步骤 4 版权证明与声明自动保存（仅在版权必填项完整时触发）
  const handleStep4Change = async (newData: Api.Application.Step4Request) => {
    setStep4Data(newData);
    if (!isStep4Valid(newData)) {
      return;
    }
    setAutoSaveStatus('saving');
    setAutoSaveTip('正在自动保存版权信息...');
    try {
      let appId = applicationIdRef.current;
      if (!appId) {
        appId = await ensureDraftCreated();
      }
      if (appId) {
        await fetchSaveStep4(appId, newData);
        setAutoSaveStatus('saved');
        setAutoSaveTip('版权信息已自动保存');
        showToast('步骤四：版权信息已自动保存', 'success');
      }
    } catch (err: any) {
      console.error('保存步骤四版权失败:', err);
      setAutoSaveStatus('error');
      setAutoSaveTip('版权信息自动保存失败');
      showToast('版权信息保存失败', 'error');
    }
  };

  // 步骤 5 商业模式与渠道选择及备注自动保存（仅在已选择目标渠道时触发）
  const handleChannelChange = async (channels: string[], note = releaseNote) => {
    setSelectedChannels(channels);
    setReleaseNote(note);
    if (!isStep5Valid(channels)) {
      return;
    }
    setAutoSaveStatus('saving');
    setAutoSaveTip('正在自动保存发行渠道...');
    try {
      let appId = applicationIdRef.current;
      if (!appId) {
        appId = await ensureDraftCreated();
      }
      if (appId) {
        await fetchSaveStep5(appId, {
          items: channels.map((chId) => ({ channel_id: chId })),
          release_request_note: note,
        });
        setAutoSaveStatus('saved');
        setAutoSaveTip('发行渠道已自动保存');
        showToast('步骤五：发行渠道已自动保存', 'success');
      }
    } catch (err: any) {
      console.error('保存步骤五发行渠道失败:', err);
      setAutoSaveStatus('error');
      setAutoSaveTip('发行渠道自动保存失败');
      showToast('发行渠道保存失败', 'error');
    }
  };

  // 演职员表变更自动保存（在步骤一必填条件满足时触发）
  const handleCastChange = (newCastList: CastMemberInfo[]) => {
    setCastList(newCastList);
    if (step1TimerRef.current) clearTimeout(step1TimerRef.current);
    step1TimerRef.current = setTimeout(async () => {
      if (!isStep1Valid(step1Data)) {
        return;
      }
      const appId = await ensureDraftCreated(step1Data, newCastList);
      if (appId) {
        setAutoSaveStatus('saving');
        setAutoSaveTip('正在自动保存演员信息...');
        try {
          const payload = getCombinedStep1Request(step1Data, newCastList);
          await fetchSaveStep1(appId, payload);
          setAutoSaveStatus('saved');
          setAutoSaveTip('演员信息已自动保存');
          showToast('演员阵容信息已自动保存', 'success');
        } catch (err) {
          console.error('保存演员信息失败:', err);
          setAutoSaveStatus('error');
          setAutoSaveTip('演员信息自动保存失败');
          showToast('演员信息保存失败', 'error');
        }
      }
    }, 600);
  };

  // 步骤 6 确认声明（协议勾选）自动保存（仅在协议勾选为 true 时触发）
  const handleAgreementChange = async (checked: boolean) => {
    setAgreed(checked);
    if (!isStep6Valid(checked)) {
      return;
    }
    setAutoSaveStatus('saving');
    setAutoSaveTip('正在自动保存确认声明...');
    try {
      let appId = applicationIdRef.current;
      if (!appId) {
        appId = await ensureDraftCreated();
      }
      if (appId) {
        await fetchSaveStep6(appId, {
          application_declaration_confirmed: true,
          operation_authorization_confirmed: true,
          privacy_confirmed: true,
        });
        setAutoSaveStatus('saved');
        setAutoSaveTip('确认声明已自动保存');
        showToast('步骤六：确认声明已自动保存', 'success');
      }
    } catch (err: any) {
      console.error('保存步骤六确认声明失败:', err);
      setAutoSaveStatus('error');
      setAutoSaveTip('确认声明自动保存失败');
    }
  };

  // 手动保存草稿
  const handleSaveDraft = async () => {
    setErrorMessage('');
    if (!isStep1Valid(step1Data)) {
      const msg = '保存草稿失败：请填写完整基础信息必填项（作品名称、内容类型、源语种、画幅朝向、受众群体、题材分类、关联合同及剧集描述）';
      setErrorMessage(msg);
      showToast(msg, 'error');
      return;
    }
    setSubmitStatus('saving');
    try {
      let appId = applicationIdRef.current;
      const step1Payload = getCombinedStep1Request();

      let isNewDraft = false;
      if (!appId) {
        const createRes = await fetchCreateApplicationDraft(step1Payload);
        if (createRes?.id) {
          appId = createRes.id;
          updateApplicationId(appId);
          isNewDraft = true;
        }
      }

      if (appId) {
        switch (activeStep) {
          case 0:
            if (!isNewDraft) {
              await fetchSaveStep1(appId, step1Payload);
            }
            break;
          case 1:
            await fetchSaveStep2(appId, {
              cover_asset_id: step1Data.cover_asset_id,
              attachments: step2Data.attachments || [],
            });
            break;
          case 2:
            await fetchSaveStep3(appId, step3Data);
            break;
          case 3:
            await fetchSaveStep4(appId, step4Data);
            break;
          case 4:
            await fetchSaveStep5(appId, {
              items: selectedChannels.map((chId) => ({ channel_id: chId })),
              release_request_note: releaseNote,
            });
            break;
          case 5:
            if (!isNewDraft) {
              await fetchSaveStep1(appId, step1Payload);
            }
            if (agreed) {
              await fetchSaveStep6(appId, {
                application_declaration_confirmed: true,
                operation_authorization_confirmed: true,
                privacy_confirmed: true,
              });
            }
            break;
        }
      }

      setAutoSaveStatus('saved');
      setAutoSaveTip('草稿保存成功');
      showToast('草稿保存成功！', 'success');
    } catch (err: any) {
      console.error('保存草稿失败:', err);
      const msg = err?.message || '保存草稿失败，请检查填写内容';
      setErrorMessage(msg);
      showToast(msg, 'error');
    } finally {
      setSubmitStatus('idle');
    }
  };

  // 提交发行申请
  const handleSubmit = async () => {
    if (!agreed) {
      showToast('请勾选协议并确认合规声明', 'error');
      return;
    }
    setErrorMessage('');
    if (!step1Data.title.trim()) {
      const msg = '请填写必填项：剧集名称';
      setErrorMessage(msg);
      showToast(msg, 'error');
      return;
    }

    setSubmitStatus('submitting');
    try {
      let appId = applicationIdRef.current;
      const step1Payload = getCombinedStep1Request();

      if (!appId) {
        const createRes = await fetchCreateApplicationDraft(step1Payload);
        if (createRes?.id) {
          appId = createRes.id;
          updateApplicationId(appId);
        }
      } else {
        await fetchSaveStep1(appId, step1Payload);
      }

      if (appId) {
        await fetchSaveStep2(appId, {
          cover_asset_id: step1Data.cover_asset_id,
          attachments: step2Data.attachments,
        });
        await fetchSaveStep3(appId, step3Data);
        await fetchSaveStep4(appId, step4Data);
        if (selectedChannels.length > 0) {
          await fetchSaveStep5(appId, {
            items: selectedChannels.map((chId) => ({ channel_id: chId })),
            release_request_note: releaseNote,
          });
        }
        // 保存步骤六确认声明 (PUT /applications/{id}/step6)
        await fetchSaveStep6(appId, {
          application_declaration_confirmed: agreed,
          operation_authorization_confirmed: agreed,
          privacy_confirmed: agreed,
        });
        // 提交审核 (POST /applications/{id}/submit)
        await fetchSubmitApplication(appId);
      }

      showToast('提交审核成功！正在为您跳转到发行申请列表...', 'success');
      setSubmitStatus('success');
      setTimeout(() => {
        navigate('/applications');
      }, 1200);
    } catch (err: any) {
      console.error('提交申请失败:', err);
      const msg = err?.message || '提交申请失败，请查看填写信息';
      setErrorMessage(msg);
      showToast(msg, 'error');
      setSubmitStatus('idle');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />

      {/* Toast 消息提示 (Rendered into document.body via Portal) */}
      {toast && (
        <Toast
          message={toast.text}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="pt-28 sm:pt-32">
        <div className="w-full max-w-[1400px] mx-auto px-6 md:px-12 py-6">
          {/* Breadcrumb & Auto-save Status */}
          <div className="flex items-center justify-between gap-4 mb-4">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 pl-1 sm:pl-2">
              <Link to="/" className="hover:text-foreground-700 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/applications" className="hover:text-foreground-700 transition-colors cursor-pointer">发行申请</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-800 font-medium">新建申请</span>
            </nav>

            {/* 自动保存状态提示徽章 */}
            {autoSaveTip && (
              <div className="flex items-center gap-2 text-xs md:text-sm px-3.5 py-1.5 rounded-full bg-gray-50 border border-gray-200/60 text-foreground-600 transition-all">
                {autoSaveStatus === 'saving' && <i className="ri-loader-4-line animate-spin text-primary-500 text-base"></i>}
                {autoSaveStatus === 'saved' && <i className="ri-checkbox-circle-line text-emerald-500 text-base"></i>}
                {autoSaveStatus === 'error' && <i className="ri-error-warning-line text-rose-500 text-base"></i>}
                <span>{autoSaveTip}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Success Banner */}
      {submitStatus === 'success' && (
        <div className="w-full max-w-[1400px] mx-auto px-6 md:px-12 pb-4">
          <div className="p-4 sm:p-5 rounded-xl border border-primary-200/60 bg-primary-50/80 flex items-start gap-3">
            <i className="ri-check-line text-primary-500 text-lg mt-0.5"></i>
            <div>
              <p className="text-base font-semibold text-foreground-900">申请提交成功！</p>
              <p className="text-sm text-foreground-500 mt-1">预计 3-5 个工作日内完成审核。您可以在「发行申请」页面查看进度。</p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div className="w-full max-w-[1400px] mx-auto px-6 md:px-12 pb-4">
          <div className="p-4 sm:p-5 rounded-xl border border-red-200 bg-red-50 flex items-start gap-3 text-red-600 text-sm">
            <i className="ri-error-warning-line text-lg mt-0.5"></i>
            <div>{errorMessage}</div>
          </div>
        </div>
      )}

      {/* Step Indicator - sticky below navbar */}
      <StepIndicator activeStep={activeStep} onStepClick={handleStepClick} />

      {/* Form Sections (Single step active view) */}
      <div className="w-full max-w-[1400px] mx-auto px-6 md:px-12 pb-16 pt-6">
        {/* 步骤 1: 基础信息 */}
        {activeStep === 0 && (
          <div className="bg-white rounded-xl border border-background-200/70 p-6 md:p-8 shadow-2xs">
            <h3 className="text-lg font-bold text-foreground-900 mb-6 pb-4 border-b border-background-200/70">
              步骤 1/6：基础信息
            </h3>
            <BasicInfoForm
              data={step1Data}
              onChange={handleStep1Change}
            />
          </div>
        )}

        {/* 步骤 2: 内容上传 */}
        {activeStep === 1 && (
          <div className="bg-white rounded-xl border border-background-200/70 p-6 md:p-8 shadow-2xs">
            <h3 className="text-lg font-bold text-foreground-900 mb-6 pb-4 border-b border-background-200/70">
              步骤 2/6：内容上传
            </h3>
            <ContentUploadForm data={step2Data} onChange={handleStep2Change} />
          </div>
        )}

        {/* 步骤 3: 剧集详情 */}
        {activeStep === 2 && (
          <div className="bg-white rounded-xl border border-background-200/70 p-6 md:p-8 shadow-2xs">
            <h3 className="text-lg font-bold text-foreground-900 mb-6 pb-4 border-b border-background-200/70">
              步骤 3/6：剧集详情
            </h3>
            <EpisodeDetailsForm data={step3Data} onChange={handleStep3Change} />
          </div>
        )}

        {/* 步骤 4: 版权证明 */}
        {activeStep === 3 && (
          <div className="bg-white rounded-xl border border-background-200/70 p-6 md:p-8 shadow-2xs">
            <h3 className="text-lg font-bold text-foreground-900 mb-6 pb-4 border-b border-background-200/70">
              步骤 4/6：版权证明
            </h3>
            <CopyrightForm data={step4Data} onChange={handleStep4Change} />
          </div>
        )}

        {/* 步骤 5: 商业模式 */}
        {activeStep === 4 && (
          <div className="bg-white rounded-xl border border-background-200/70 p-6 md:p-8 shadow-2xs">
            <h3 className="text-lg font-bold text-foreground-900 mb-6 pb-4 border-b border-background-200/70">
              步骤 5/6：商业模式与渠道
            </h3>
            <BusinessModelForm
              channelList={channelList}
              loadingChannels={loadingChannels}
              selectedChannels={selectedChannels}
              onChannelChange={(ch) => handleChannelChange(ch, releaseNote)}
              releaseNote={releaseNote}
              onReleaseNoteChange={(note) => handleChannelChange(selectedChannels, note)}
            />
          </div>
        )}

        {/* 步骤 6: 演员信息 */}
        {activeStep === 5 && (
          <div className="bg-white rounded-xl border border-background-200/70 p-6 md:p-8 shadow-2xs">
            <h3 className="text-lg font-bold text-foreground-900 mb-6 pb-4 border-b border-background-200/70">
              步骤 6/6：演员信息与确认提交
            </h3>
            <CastForm castList={castList} onChange={handleCastChange} />
          </div>
        )}

        {/* 步骤导航与控制面板 */}
        <div className="mt-6 bg-white rounded-xl border border-background-200/70 p-6 md:p-8 shadow-2xs space-y-5">
          {activeStep === 5 && (
            <label className="flex items-start gap-3.5 p-4 sm:p-5 rounded-lg bg-gray-50 border border-gray-200/60 cursor-pointer">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => handleAgreementChange(e.target.checked)}
                className="mt-1 w-4.5 h-4.5 rounded border-background-300 text-primary-500 focus:ring-primary-400 cursor-pointer"
              />
              <span className="text-sm text-foreground-700 leading-relaxed">
                我确认以上信息真实有效，并同意平台的
                <a href="#" className="text-primary-600 hover:text-primary-500 font-medium mx-1" onClick={(e) => e.preventDefault()}>用户协议</a>
                和
                <a href="#" className="text-primary-600 hover:text-primary-500 font-medium mx-1" onClick={(e) => e.preventDefault()}>内容发行服务条款</a>
                。提交后信息将作为线下签约的依据。
              </span>
            </label>
          )}

          <div className="flex items-center justify-between gap-4 pt-2">
            <div>
              {activeStep > 0 ? (
                <button
                  type="button"
                  onClick={handlePrevStep}
                  className="px-6 py-3 rounded-lg border border-background-300/80 text-foreground-700 text-base font-medium hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-2 shadow-2xs"
                >
                  <i className="ri-arrow-left-line text-lg"></i>
                  <span>上一步</span>
                </button>
              ) : (
                <Link
                  to="/applications"
                  className="px-6 py-3 rounded-lg border border-background-300/80 text-foreground-700 text-base font-medium hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap shadow-2xs"
                >
                  取消
                </Link>
              )}
            </div>

            <div className="flex items-center gap-4">
              {/* <button
                type="button"
                onClick={handleSaveDraft}
                disabled={submitStatus === 'saving' || submitStatus === 'submitting'}
                className="px-6 py-3 rounded-lg border border-background-300/80 text-foreground-700 text-base font-medium hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 shadow-2xs"
              >
                {submitStatus === 'saving' ? '保存中...' : '保存草稿'}
              </button> */}

              {activeStep < 5 ? (
                <button
                  type="button"
                  onClick={handleNextStep}
                  className="px-7 py-3 rounded-lg bg-primary-500 text-foreground-950 text-base font-semibold hover:bg-primary-400 transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-2 shadow-xs"
                >
                  <span>下一步</span>
                  <i className="ri-arrow-right-line text-lg"></i>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!agreed || submitStatus === 'saving' || submitStatus === 'submitting'}
                  className="px-8 py-3 rounded-lg bg-primary-500 text-foreground-950 text-base font-semibold hover:bg-primary-400 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed shadow-xs"
                >
                  {submitStatus === 'submitting' ? '提交中...' : '提交申请'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}